import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Modal } from '@/components/ui/Modal';

interface AddAnnouncementModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    announcementToEdit?: any;
}

export const AddAnnouncementModal: React.FC<AddAnnouncementModalProps> = ({ isOpen, onClose, onSuccess, announcementToEdit }) => {
    const [titleEn, setTitleEn] = useState('');
    const [titleEs, setTitleEs] = useState('');
    const [bodyEn, setBodyEn] = useState('');
    const [bodyEs, setBodyEs] = useState('');
    const [events, setEvents] = useState<string[]>(['IE', 'ES', 'PB']);
    const [isPublished, setIsPublished] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (announcementToEdit) {
            setTitleEn(announcementToEdit.title?.en || '');
            setTitleEs(announcementToEdit.title?.es || '');
            setBodyEn(announcementToEdit.body?.en || '');
            setBodyEs(announcementToEdit.body?.es || '');
            setEvents(announcementToEdit.events || ['IE', 'ES', 'PB']);
            setIsPublished(announcementToEdit.isPublished ?? true);
        } else {
            setTitleEn('');
            setTitleEs('');
            setBodyEn('');
            setBodyEs('');
            setEvents(['IE', 'ES', 'PB']);
            setIsPublished(true);
        }
    }, [announcementToEdit, isOpen]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const data = {
                title: { en: titleEn, es: titleEs },
                body: { en: bodyEn, es: bodyEs },
                events,
                isPublished,
                publishedAt: announcementToEdit?.publishedAt || Timestamp.now(),
                updatedAt: Timestamp.now(),
            };

            if (announcementToEdit) {
                await updateDoc(doc(db, 'announcements', announcementToEdit.id), data);
            } else {
                await addDoc(collection(db, 'announcements'), {
                    ...data,
                    createdAt: Timestamp.now(),
                });
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save announcement');
        } finally {
            setLoading(false);
        }
    };

    const toggleEvent = (code: string) => {
        setEvents(prev =>
            prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
        );
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={announcementToEdit ? "Edit Announcement" : "New Announcement"}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4 font-sans text-secondary">
                {error && (
                    <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-100 text-base">
                        {error}
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Title (EN)</label>
                        <input
                            type="text"
                            required
                            className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                            value={titleEn}
                            onChange={e => setTitleEn(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Title (ES)</label>
                        <input
                            type="text"
                            required
                            className="w-full p-2.5 border border-gray-200 rounded-lg outline-none"
                            value={titleEs}
                            onChange={e => setTitleEs(e.target.value)}
                        />
                    </div>
                </div>

                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Body (EN)</label>
                    <textarea
                        required
                        className="w-full p-2.5 border border-gray-200 rounded-lg outline-none min-h-[100px]"
                        value={bodyEn}
                        onChange={e => setBodyEn(e.target.value)}
                    />
                </div>

                <div className="space-y-1">
                    <label className="block text-sm font-bold text-secondary-light uppercase tracking-widest">Body (ES)</label>
                    <textarea
                        required
                        className="w-full p-2.5 border border-gray-200 rounded-lg outline-none min-h-[100px]"
                        value={bodyEs}
                        onChange={e => setBodyEs(e.target.value)}
                    />
                </div>

                <div className="flex justify-between items-center bg-bg-subtle p-3 rounded-lg border border-gray-100">
                    <div className="space-y-2">
                        <span className="block text-[10px] font-bold text-secondary-light uppercase tracking-widest">Target Events</span>
                        <div className="flex gap-4">
                            {['IE', 'ES', 'PB'].map(code => (
                                <label key={code} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={events.includes(code)}
                                        onChange={() => toggleEvent(code)}
                                        className="rounded text-primary focus:ring-primary h-4 w-4"
                                    />
                                    <span className="text-sm font-medium">{code === 'IE' ? 'Ireland' : code === 'PB' ? 'Pre-Boda' : 'Spain'}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isPublished}
                            onChange={e => setIsPublished(e.target.checked)}
                            className="rounded text-primary focus:ring-primary h-4 w-4"
                        />
                        <span className="text-sm font-bold uppercase tracking-wider text-secondary">Published</span>
                    </label>
                </div>

                <div className="flex justify-end gap-3 mt-4">
                    <button type="button" className="px-6 py-2 rounded-full text-base font-bold border border-gray-200 hover:bg-gray-50 transition-colors" onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button type="submit" className="px-6 py-2 rounded-full text-base font-bold bg-primary text-white hover:bg-primary-dark transition-all shadow-md active:scale-95" disabled={loading}>
                        {loading ? 'Saving...' : (announcementToEdit ? 'Update' : 'Publish')}
                    </button>
                </div>
            </form>
        </Modal>
    );
};
